import { useRef, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { ChevronDown, ChevronUp, Plus, Trash2, X } from 'lucide-react';
import { useSubmitProduct, useUpdateProduct, useDeactivateProduct, safeMessage } from '@/lib/admin';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ChannelChip } from '@/components/ChannelChip';
import { cn } from '@/lib/utils';
import type { AdminProduct, Supplier } from '@/lib/types';

const UNITS = ['case', 'lb', 'kg', 'each', 'dozen', 'bag', 'gallon', 'qt', 'loaf'];

interface FallbackRow {
  key: number;
  supplierId: string;
  sku: string;
}

interface ProductFormDialogProps {
  open: boolean;
  onClose: () => void;
  product: AdminProduct | null;
  suppliers: Supplier[];
  categories: string[];
}

let rowKey = 0;
const toRows = (product: AdminProduct | null): FallbackRow[] =>
  (product?.fallbacks ?? []).map((f) => ({ key: ++rowKey, supplierId: f.supplierId, sku: f.sku }));

// Add / edit product: details, primary supplier routing, and a ranked list of
// fallback suppliers tried in order when the primary back-orders.
export function ProductFormDialog({ open, onClose, product, suppliers, categories }: ProductFormDialogProps) {
  const createMutation = useSubmitProduct();
  const updateMutation = useUpdateProduct();
  const deactivateMutation = useDeactivateProduct();

  const [name, setName] = useState('');
  const [category, setCategory] = useState('');
  const [unitOfMeasure, setUnitOfMeasure] = useState('case');
  const [primarySupplierId, setPrimarySupplierId] = useState('');
  const [primarySku, setPrimarySku] = useState('');
  const [fallbacks, setFallbacks] = useState<FallbackRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [confirmDeactivate, setConfirmDeactivate] = useState(false);
  const bodyRef = useRef<HTMLDivElement>(null);

  // Reset the form whenever the dialog opens on a different target.
  const [lastTarget, setLastTarget] = useState<{ open: boolean; product: AdminProduct | null } | null>(null);
  if (!lastTarget || lastTarget.open !== open || lastTarget.product !== product) {
    setLastTarget({ open, product });
    if (open) {
      setName(product?.name ?? '');
      setCategory(product?.category ?? '');
      setUnitOfMeasure(product?.unitOfMeasure ?? 'case');
      setPrimarySupplierId(product?.primarySupplierId ?? '');
      setPrimarySku(product?.primarySku ?? '');
      setFallbacks(toRows(product));
      setError(null);
      setConfirmDeactivate(false);
    }
  }

  const supplierById = new Map(suppliers.map((s) => [s.id, s]));
  const usedIds = new Set([primarySupplierId, ...fallbacks.map((f) => f.supplierId)]);
  const canAddFallback = suppliers.some((s) => !usedIds.has(s.id));
  const incompleteFallback = fallbacks.some((f) => !f.supplierId || !f.sku.trim());
  const canSave = name.trim() && unitOfMeasure.trim() && primarySupplierId && primarySku.trim() && !incompleteFallback;
  const saving = createMutation.isPending || updateMutation.isPending;

  function changePrimary(id: string) {
    setPrimarySupplierId(id);
    // A supplier can't be both primary and fallback.
    setFallbacks((rows) => rows.filter((r) => r.supplierId !== id));
  }

  function updateRow(key: number, patch: Partial<FallbackRow>) {
    setFallbacks((rows) => rows.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  }

  function moveRow(index: number, delta: -1 | 1) {
    setFallbacks((rows) => {
      const next = [...rows];
      const [row] = next.splice(index, 1);
      next.splice(index + delta, 0, row);
      return next;
    });
  }

  function addRow() {
    const firstFree = suppliers.find((s) => !usedIds.has(s.id));
    setFallbacks((rows) => [...rows, { key: ++rowKey, supplierId: firstFree?.id ?? '', sku: '' }]);
    // Bring the new row into view.
    requestAnimationFrame(() => bodyRef.current?.scrollTo({ top: bodyRef.current.scrollHeight, behavior: 'smooth' }));
  }

  async function handleSave() {
    setError(null);
    const payload = {
      name: name.trim(),
      category: category.trim() || null,
      unitOfMeasure: unitOfMeasure.trim(),
      primarySupplierId,
      primarySku: primarySku.trim(),
      fallbacks: fallbacks.map((f) => ({ supplierId: f.supplierId, sku: f.sku.trim() })),
    };
    try {
      if (product) {
        await updateMutation.mutateAsync({ id: product.id, ...payload });
      } else {
        await createMutation.mutateAsync(payload);
      }
      onClose();
    } catch (err) {
      setError(safeMessage(err));
    }
  }

  async function handleDeactivate() {
    if (!product) return;
    try {
      await deactivateMutation.mutateAsync(product.id);
      onClose();
    } catch (err) {
      setError(safeMessage(err));
    }
  }

  return (
    <Dialog.Root open={open} onOpenChange={(o) => !o && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/70 backdrop-blur-sm data-[state=open]:animate-[fadeIn_150ms_ease-out]" />
        <Dialog.Content
          aria-describedby={undefined}
          className="fixed left-1/2 top-1/2 z-50 flex max-h-[92vh] w-[calc(100vw-2rem)] max-w-3xl -translate-x-1/2 -translate-y-1/2 flex-col border border-line bg-surface shadow-card focus:outline-none data-[state=open]:animate-[popIn_160ms_ease-out]"
        >
          {/* Header */}
          <div className="flex items-start justify-between gap-4 border-b border-line px-6 py-5">
            <div className="min-w-0">
              <span className="eyebrow">{product ? 'Edit product' : 'New product'}</span>
              <Dialog.Title className="truncate font-head text-2xl font-extrabold uppercase text-ink">
                {name.trim() || (product ? product.name : 'Add Product')}
              </Dialog.Title>
            </div>
            <Dialog.Close asChild>
              <button className="rounded p-1.5 text-muted hover:bg-surface-3 hover:text-ink" aria-label="Close">
                <X className="h-5 w-5" />
              </button>
            </Dialog.Close>
          </div>

          {/* Body */}
          <div ref={bodyRef} className="flex-1 space-y-8 overflow-y-auto px-6 py-6">
            <Section title="Product details">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-6">
                <Field label="Product name" className="sm:col-span-6">
                  <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Heavy Cream" autoFocus />
                </Field>
                <Field label="Category" className="sm:col-span-3">
                  <Input
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    placeholder="e.g. Dairy"
                    list="product-categories"
                  />
                  <datalist id="product-categories">
                    {categories.map((c) => (
                      <option key={c} value={c} />
                    ))}
                  </datalist>
                </Field>
                <Field label="Unit of measure" className="sm:col-span-3">
                  <Input
                    value={unitOfMeasure}
                    onChange={(e) => setUnitOfMeasure(e.target.value)}
                    list="product-units"
                  />
                  <datalist id="product-units">
                    {UNITS.map((u) => (
                      <option key={u} value={u} />
                    ))}
                  </datalist>
                </Field>
              </div>
            </Section>

            <Section title="Primary supplier" hint="Every order for this product is routed here first.">
              <div className="grid grid-cols-1 gap-4 border border-line bg-surface-2/50 p-4 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_6.5rem] sm:items-end">
                <Field label="Supplier">
                  <SupplierSelect
                    value={primarySupplierId}
                    onChange={changePrimary}
                    suppliers={suppliers}
                    placeholder="Select a supplier…"
                  />
                </Field>
                <Field label="Supplier SKU / code">
                  <Input value={primarySku} onChange={(e) => setPrimarySku(e.target.value)} className="font-mono" />
                </Field>
                <div className="flex h-10 items-center">
                  <ChannelChip channel={supplierById.get(primarySupplierId)?.dispatchChannel} />
                </div>
              </div>
            </Section>

            <Section
              title="Fallback suppliers"
              hint="If the primary supplier back-orders, the line is rerouted to the first fallback, then the next, in this order."
              action={
                <Button
                  variant="ghost"
                  size="sm"
                  className="shrink-0 whitespace-nowrap"
                  onClick={addRow}
                  disabled={!canAddFallback || !primarySupplierId}
                >
                  <Plus className="h-3.5 w-3.5" /> Add fallback
                </Button>
              }
            >
              {fallbacks.length === 0 ? (
                <div className="border border-dashed border-line px-4 py-8 text-center text-sm text-muted">
                  No fallback suppliers. Back-orders on this product will need manual sourcing.
                </div>
              ) : (
                <ol className="space-y-2">
                  {fallbacks.map((row, i) => {
                    const options = suppliers.filter(
                      (s) => s.id === row.supplierId || !usedIds.has(s.id),
                    );
                    return (
                      <li
                        key={row.key}
                        className="grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-3 border border-line bg-surface-2/50 p-3 sm:grid-cols-[auto_minmax(0,1fr)_minmax(0,1fr)_6.5rem_auto]"
                      >
                        <span className="flex h-7 w-7 items-center justify-center rounded-full border border-brand/50 font-mono text-xs text-brand">
                          {i + 1}
                        </span>
                        <SupplierSelect
                          value={row.supplierId}
                          onChange={(id) => updateRow(row.key, { supplierId: id })}
                          suppliers={options}
                          placeholder="Select…"
                          aria-label={`Fallback ${i + 1} supplier`}
                        />
                        <Input
                          value={row.sku}
                          onChange={(e) => updateRow(row.key, { sku: e.target.value })}
                          placeholder="Supplier SKU"
                          aria-label={`Fallback ${i + 1} SKU`}
                          className={cn('col-span-2 font-mono sm:col-span-1', !row.sku.trim() && 'border-amber/50')}
                        />
                        <div className="hidden sm:block">
                          <ChannelChip channel={supplierById.get(row.supplierId)?.dispatchChannel} />
                        </div>
                        <div className="col-start-3 row-start-1 flex items-center sm:col-start-auto sm:row-start-auto">
                          <IconButton label="Move up" disabled={i === 0} onClick={() => moveRow(i, -1)}>
                            <ChevronUp className="h-4 w-4" />
                          </IconButton>
                          <IconButton label="Move down" disabled={i === fallbacks.length - 1} onClick={() => moveRow(i, 1)}>
                            <ChevronDown className="h-4 w-4" />
                          </IconButton>
                          <IconButton
                            label="Remove fallback"
                            danger
                            onClick={() => setFallbacks((rows) => rows.filter((r) => r.key !== row.key))}
                          >
                            <Trash2 className="h-4 w-4" />
                          </IconButton>
                        </div>
                      </li>
                    );
                  })}
                </ol>
              )}
            </Section>

            {error && (
              <p className="border border-danger/40 bg-danger/10 px-3 py-2 text-sm text-danger" role="alert">
                {error}
              </p>
            )}
          </div>

          {/* Footer */}
          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-line bg-surface-2/40 px-6 py-4">
            {product ? (
              confirmDeactivate ? (
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-muted">Remove from the catalog?</span>
                  <Button variant="danger" size="sm" onClick={handleDeactivate} disabled={deactivateMutation.isPending}>
                    Deactivate
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setConfirmDeactivate(false)}>
                    Keep
                  </Button>
                </div>
              ) : (
                <button
                  className="text-sm text-muted underline-offset-4 hover:text-danger hover:underline"
                  onClick={() => setConfirmDeactivate(true)}
                >
                  Deactivate product
                </button>
              )
            ) : (
              <span />
            )}
            <div className="flex gap-2">
              <Button variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={!canSave || saving}>
                {saving ? 'Saving…' : product ? 'Save changes' : 'Create product'}
              </Button>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function Section({
  title,
  hint,
  action,
  children,
}: {
  title: string;
  hint?: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section>
      <div className="mb-3 flex items-end justify-between gap-4">
        <div className="min-w-0">
          <h3 className="section-head">{title}</h3>
          {hint && <p className="mt-1 text-xs text-muted">{hint}</p>}
        </div>
        {action}
      </div>
      {children}
    </section>
  );
}

function Field({ label, className, children }: { label: string; className?: string; children: React.ReactNode }) {
  return (
    <label className={cn('block', className)}>
      <span className="mb-1.5 block text-xs font-medium uppercase tracking-wider text-muted">{label}</span>
      {children}
    </label>
  );
}

function SupplierSelect({
  value,
  onChange,
  suppliers,
  placeholder,
  ...rest
}: {
  value: string;
  onChange: (id: string) => void;
  suppliers: Supplier[];
  placeholder: string;
  'aria-label'?: string;
}) {
  return (
    <select
      {...rest}
      className="h-10 w-full rounded-card border border-line bg-surface-2 px-3 text-sm text-ink focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
      value={value}
      onChange={(e) => onChange(e.target.value)}
    >
      {!value && <option value="">{placeholder}</option>}
      {suppliers.map((s) => (
        <option key={s.id} value={s.id}>
          {s.name}
        </option>
      ))}
    </select>
  );
}

function IconButton({
  label,
  disabled,
  danger,
  onClick,
  children,
}: {
  label: string;
  disabled?: boolean;
  danger?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        'rounded p-1.5 text-muted transition-colors hover:bg-surface-3 disabled:pointer-events-none disabled:opacity-30',
        danger ? 'hover:text-danger' : 'hover:text-brand',
      )}
    >
      {children}
    </button>
  );
}
